import { cn } from "@/lib/utils";

// The donor's loading block — MUI's `Skeleton`, `variant='rounded'` with
// `animation='wave'` (`Metadata/MetaLoadingSkeleton.tsx:9-11`).
//
// **Here rather than beside the page that draws it** because it is a shared
// donor component met on a migration-target screen, which ports as a shared
// Atlas component (`docs/agents/migration-guidelines.md`, "Shared donor
// components port as shared Atlas components"). It is an app-authored sibling
// of the vendored primitives, not an edit to one.
//
// **Not `Skeleton` from `skeleton.tsx`**, which is the vendored shadcn block and
// paints a different thing: `bg-accent animate-pulse rounded-md` — a whole-box
// opacity pulse at the app's own `--radius`. The donor's is a gradient sweeping
// across a clipped box at a flat 4px. Both are "a skeleton"; only one is the one
// modsy shows. The vendored file is not edited to match, because it is the
// upstream file and other screens are entitled to the upstream look.
//
// Every value is a MUI default rather than a decision: the donor's theme
// overrides neither `MuiSkeleton` nor `shape.borderRadius`, and opts into
// neither `reducedMotion` nor `cssVariables` (`packages/react-ui/src/theme/
// theme.ts`), so the component is whatever MUI ships — ground
// `alpha(text.primary, 0.11)` on the light branch, radius `shape.borderRadius`
// (4px), sweep `linear-gradient(90deg, transparent, action.hover, transparent)`
// under `animation: wave 2s linear 0.5s infinite`. No reduced-motion block
// either: `resolveReducedMotionStyles` returns `null` for a theme that asks for
// neither `'always'` nor `'system'`.
//
// **The version is load-bearing.** These are the values in `@mui/material`
// **9.2.0**, which is what the donor's lockfile resolves `^9.0.1` to. An
// earlier cut of this file ported MUI *5*'s wave from memory and got the
// duration (1.6s, not 2s) and the keyframe midpoint (60%, not 50%) wrong, and
// dropped the Safari mask below. Read the installed file, not the defaults you
// remember.
interface MuiSkeletonProps extends React.ComponentProps<"div"> {
  /**
   * MUI's `variant`, of the two shapes this app has met.
   *
   * `text` is MUI's default and the one the donor's table draws: the box is
   * laid out at full height and then squashed to 60% on the y-axis, so it reads
   * as a line of type rather than a bar, and its corner radius is pre-divided
   * by the same 0.60 so the corner comes out circular *after* the scale
   * (`Skeleton.js:104-118`). `rounded` is the plain 4px block the detail page's
   * three loaders use. `rectangular` is the same block with no radius — the
   * spec lines' row blocks (`SpecDetailsIsland/components/Skeletons.tsx:93-104`).
   */
  variant?: "text" | "rounded" | "rectangular";
  /**
   * MUI's `animation`. `pulse` is MUI's default — a flat opacity fade on the
   * box — and is what the donor's table asks for by name, overriding the wave
   * MRT would otherwise pass (`table.tsx:226-228`). `wave` is the gradient
   * sweeping across a clipped box, which is what the metadata skeletons use
   * (`Metadata/MetaLoadingSkeleton.tsx:9-11`).
   */
  animation?: "pulse" | "wave";
}

export function MuiSkeleton({
  className,
  variant = "text",
  animation = "pulse",
  ...props
}: MuiSkeletonProps) {
  const wave = animation === "wave";
  return (
    <div
      data-slot="mui-skeleton"
      data-variant={variant}
      data-animation={animation}
      className={cn(
        "bg-skeleton-ground relative overflow-hidden rounded-[4px]",
        // `variant='text'`: `height: auto`, no margins, and the squash. The
        // `:empty:before` non-breaking space is MUI's — it is what gives an
        // empty skeleton a line box *before* the scale takes 40% back off. It
        // rides a custom property (`--mui-skeleton-line`, `index.css`) because
        // an arbitrary `content-['\00a0']` never matched its generated rule.
        variant === "text" &&
          "my-0 h-auto rounded-[4px/6.7px] empty:before:content-(--mui-skeleton-line)",
        // Spelled as arbitrary properties rather than `scale-y-60` + `origin-*`
        // because Tailwind v4's scale utilities emit the standalone `scale`
        // property, and MUI's is a `transform` — a difference the composited
        // result hides but `getComputedStyle` does not.
        variant === "text" && "[transform-origin:0_55%] [transform:scale(1,0.6)]",
        variant === "rectangular" && "rounded-none",
        wave ? "animate-none" : "animate-mui-skeleton-pulse",
        // `Skeleton.js:172` — "Fix bug in Safari
        // https://bugs.webkit.org/show_bug.cgi?id=68196". `overflow-hidden`
        // alone does not clip the sweeping `::after` to the 4px corners in
        // WebKit, so without this the gradient bleeds past them in Safari.
        "[-webkit-mask-image:-webkit-radial-gradient(white,black)]",
        // The sweep. `-translate-x-full` is the resting position the 0.5s delay
        // holds it at before the first pass, which is MUI's own initial
        // transform rather than a first frame borrowed from the keyframe.
        wave && "after:absolute after:inset-0 after:-translate-x-full after:content-['']",
        // `/srgb` because MUI's is a plain sRGB ramp and Tailwind v4 interpolates
        // in oklab by default, which bends the midpoint of a sweep whose two
        // endpoints are both transparent.
        wave &&
          "after:via-skeleton-sheen after:bg-linear-to-r/srgb after:from-transparent after:to-transparent",
        wave && "after:animate-mui-skeleton-wave",
        className,
      )}
      {...props}
    />
  );
}
